import type { Party } from '../../shared/interfaces';

export interface PartyLookupResult {
  isValid: boolean;
  error: string;
  partyName: string;
  partyId: number | null;
  partyCode: string;
  currentBalance?: number;
}

export function extractPartyCodeFromInput(value: string): string {
  const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
  return codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
}

export function validatePartyCodeFormat(code: string): { isValid: boolean; error: string } {
  if (!code || code.trim().length === 0) {
    return { isValid: false, error: 'Party code is required' };
  }
  if (code.length > 5) {
    return { isValid: false, error: 'Party code must be at most 5 characters' };
  }
  if (!/^[a-zA-Z0-9]+$/.test(code)) {
    return { isValid: false, error: 'Party code must be alphanumeric' };
  }
  return { isValid: true, error: '' };
}

export async function lookupPartyByCode(
  code: string,
  parties: Party[],
  fetchByCode: (code: string) => Promise<Party>,
): Promise<PartyLookupResult> {
  const trimmedCode = extractPartyCodeFromInput(code);
  const formatValidation = validatePartyCodeFormat(trimmedCode);
  if (!formatValidation.isValid) {
    return {
      isValid: false,
      error: formatValidation.error,
      partyName: '',
      partyId: null,
      partyCode: '',
    };
  }

  const filteredParties = (parties || []).filter((p) => p.id !== 1);
  const foundParty = filteredParties.find((p) => p.partyCode.toUpperCase() === trimmedCode);

  if (foundParty) {
    return {
      isValid: true,
      error: '',
      partyName: foundParty.partyName,
      partyId: foundParty.id,
      partyCode: foundParty.partyCode,
      currentBalance: foundParty.currentBalance,
    };
  }

  try {
    const partyData = await fetchByCode(trimmedCode);
    if (partyData?.id && partyData.id !== 1) {
      return {
        isValid: true,
        error: '',
        partyName: partyData.partyName || '',
        partyId: partyData.id,
        partyCode: partyData.partyCode,
        currentBalance: partyData.currentBalance,
      };
    }
  } catch {
    // fall through
  }

  return {
    isValid: false,
    error: 'Invalid party code',
    partyName: '',
    partyId: null,
    partyCode: '',
  };
}
